import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useHarnessStore } from '@/store/useHarnessStore';
import { harnessService } from '@/services/api/harnessService';
import type { MeasurementDto } from '@/features/harness/types';
import { MeasurementListView } from './MeasurementListView';

/**
 * Live-harness Measurement list: binds {@link MeasurementListView} to the store (recorded
 * Measurements, the in-progress indicator, and the multi-select selection from Story 2.1) and
 * owns the Story 1.6 delete flow — a confirmation dialog that hard-deletes the Measurement and
 * drops it from the store. The read-only review view (Story 3.1) reuses `MeasurementListView`
 * directly with fetched data and no delete affordance.
 */
export function MeasurementList() {
  const activeBatch = useHarnessStore((s) => s.activeBatch);
  const measurements = useHarnessStore((s) => s.measurements);
  const inProgressMeasurement = useHarnessStore((s) => s.inProgressMeasurement);
  const selectedMeasurementIds = useHarnessStore((s) => s.selectedMeasurementIds);
  const toggleMeasurementSelection = useHarnessStore((s) => s.toggleMeasurementSelection);
  const selectAllMeasurements = useHarnessStore((s) => s.selectAllMeasurements);
  const clearMeasurementSelection = useHarnessStore((s) => s.clearMeasurementSelection);
  const removeMeasurement = useHarnessStore((s) => s.removeMeasurement);

  const [deleteConfirmMeasurement, setDeleteConfirmMeasurement] = useState<MeasurementDto | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Open/close the confirmation dialog through these so a previous failure's error never bleeds
  // into a fresh confirmation: `deleteError` is cleared on every open and close. (Closing is
  // ignored while a delete is in flight so a backdrop/escape can't dismiss an in-progress request.)
  const openDeleteConfirm = (measurement: MeasurementDto) => {
    setDeleteError(null);
    setDeleteConfirmMeasurement(measurement);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteError(null);
    setDeleteConfirmMeasurement(null);
  };

  const handleDeleteConfirm = async () => {
    if (!activeBatch || !deleteConfirmMeasurement) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await harnessService.deleteMeasurement(activeBatch.id, deleteConfirmMeasurement.id);
      removeMeasurement(deleteConfirmMeasurement.id);
      setDeleteConfirmMeasurement(null);
    } catch {
      setDeleteError('Failed to delete Measurement. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <MeasurementListView
        measurements={measurements}
        selectedIds={selectedMeasurementIds}
        onToggleSelection={toggleMeasurementSelection}
        onSelectAll={selectAllMeasurements}
        onClearSelection={clearMeasurementSelection}
        inProgress={inProgressMeasurement}
        onRequestDelete={openDeleteConfirm}
      />

      <Dialog open={!!deleteConfirmMeasurement} onClose={closeDeleteConfirm}>
        <DialogTitle>Delete Measurement</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete measurement{' '}
            <strong>#{deleteConfirmMeasurement?.sequenceNumber}</strong>? This permanently
            removes it and all its recorded Communication Log frames — this cannot be undone.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
